// Planning helpers extracted from SpecialEdApp.jsx

export function parseLessonText(t) {
  const m = /Lesson\s+(\d+)\s+of\s+(\d+)/i.exec(t || '')
  return { current: m ? parseInt(m[1], 10) : 1, total: m ? parseInt(m[2], 10) : 1 }
}

export function parseUnitNum(u) {
  const m = /Unit\s+(\d+)/i.exec(u || '')
  return m ? parseInt(m[1], 10) : 1
}

export function subjectKey(c) {
  return `${c.subject}`.toLowerCase()
}

export function parseGradeId(subject) {
  const s = subject || ''
  const m0 = /^(K|\d+)/i.exec(s.trim())
  if (m0) return m0[1].toLowerCase() === 'k' ? 'gradek' : `grade${parseInt(m0[1], 10)}`
  const m = /(K|\d+)(?:st|nd|rd|th)?\s+Grade/i.exec(s)
  return m ? (m[1].toLowerCase() === 'k' ? 'gradek' : `grade${parseInt(m[1], 10)}`) : null
}

export function extractElaUnitSubtitle(title) {
  if (!title || typeof title !== 'string') return null
  const lastColon = title.lastIndexOf(':')
  if (lastColon !== -1 && lastColon < title.length - 1) {
    return title.slice(lastColon + 1).trim()
  }
  const m = title.match(/Unit\s*\d+\s*:\s*(?:Lesson\s*\d+\s*:)?\s*(.+)$/i)
  return m ? m[1].trim() : null
}

export function buildElaData() {
  let ctx
  try {
    ctx = require.context('../data/ela', true, /\.json$/)
  } catch {
    return { totals: {}, titles: {}, unitTitles: {} }
  }
  const totals = {}
  const titles = {}
  const unitSubtitleCounts = {}
  ctx.keys().forEach(k => {
    const clean = k.replace(/^\.\//, '')
    const parts = clean.split('/')
    if (parts.length < 3) return
    const grade = parts[0]
    const unit = parts[1]
    const key = `${grade}/${unit}`
    totals[key] = (totals[key] || 0) + 1
    try {
      const data = ctx(k)
      const filename = parts[parts.length - 1]
      const m = filename.match(/(?:lesson|_l)(\d+)/i)
      const n = m ? parseInt(m[1], 10) : (typeof data?.lesson === 'number' ? data.lesson : null)
      if (n) {
        titles[`${grade}/${unit}/lesson${n}`] = data?.title || `Lesson ${n}`
      }
      const sub = extractElaUnitSubtitle(data?.title)
      if (sub) {
        const k2 = `${grade}/${unit}`
        if (!unitSubtitleCounts[k2]) unitSubtitleCounts[k2] = {}
        unitSubtitleCounts[k2][sub] = (unitSubtitleCounts[k2][sub] || 0) + 1
      }
    } catch {}
  })
  const unitTitles = {}
  Object.keys(totals).forEach(k => {
    const unit = k.split('/')[1]
    const unitNum = parseInt(unit.replace(/\D/g, ''), 10) || ''
    const counts = unitSubtitleCounts[k] || {}
    const best = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]
    unitTitles[k] = best ? `Unit ${unitNum}: ${best[0]}` : `Unit ${unitNum}`
  })
  return { totals, titles, unitTitles }
}

export function buildSsData() {
  let ctx
  try {
    ctx = require.context('../data/ss', true, /\.json$/)
  } catch {
    return { totals: {}, totalsNumeric: {}, seq: {}, titles: {}, numIndex: {} }
  }
  const totals = {}
  const totalsNumeric = {}
  const seq = {}
  const titles = {}
  const numIndex = {}
  ctx.keys().forEach(k => {
    const clean = k.replace(/^\.\//, '')
    const parts = clean.split('/')
    if (parts.length < 3) return
    const grade = parts[0]
    const unit = parts[1]
    const key = `${grade}/${unit}`
    if (!seq[key]) seq[key] = []
    try {
      const data = ctx(k)
      const filename = parts[parts.length - 1]
      let id
      let order = 999
      if (/_l0\.json$/i.test(filename)) { id = 'lesson0'; order = 0 }
      else if (/_li\.json$/i.test(filename)) { id = 'li'; order = 0.5 }
      else if (/_lw\.json$/i.test(filename)) { id = 'lw'; order = 999 }
      else {
        const m = filename.match(/_l(\d+)\.json$/i)
        if (m) { id = `lesson${parseInt(m[1], 10)}`; order = parseInt(m[1], 10) }
        else if (typeof data?.lesson === 'number') { id = `lesson${data.lesson}`; order = data.lesson }
        else { id = filename.replace(/\.json$/, '') }
      }
      titles[`${grade}/${unit}/${id}`] = data?.title || id.toUpperCase()
      seq[key].push({ id, order, number: (/^lesson(\d+)$/i.test(id) ? parseInt(id.replace(/\D/g, ''), 10) : null) })
    } catch {}
  })
  Object.keys(seq).forEach(key => {
    seq[key].sort((a,b) => a.order - b.order)
    totals[key] = seq[key].length
    const mapNI = {}
    seq[key].forEach((item, idx) => { if (typeof item.number === 'number' && mapNI[item.number] == null) mapNI[item.number] = idx })
    numIndex[key] = mapNI
    const numericOnly = seq[key].filter(it => typeof it.number === 'number')
    const maxN = numericOnly.length ? Math.max(...numericOnly.map(it => it.number)) : 0
    totalsNumeric[key] = maxN
  })
  return { totals, totalsNumeric, seq, titles, numIndex }
}

export function weekdayIndexToday(days) {
  const js = new Date().getDay()
  const name = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][js]
  const idx = days.indexOf(name)
  return idx === -1 ? 0 : idx
}

export function resolveLessonWithOffset(elaTotals, gradeId, unitNum, lessonNum, offset) {
  let u = unitNum
  let l = lessonNum + offset
  while (true) {
    const total = elaTotals[`${gradeId}/unit${u}`]
    if (!total) break
    if (l >= 1 && l <= total) return { unit: u, lesson: l }
    if (l < 1) { u -= 1; if (u < 1) return { unit: unitNum, lesson: Math.max(1, lessonNum + offset) }; const prevTotal = elaTotals[`${gradeId}/unit${u}`]; if (!prevTotal) return { unit: unitNum, lesson: Math.max(1, lessonNum + offset) }; l = prevTotal + l }
    else {
      u += 1
      const nextTotal = elaTotals[`${gradeId}/unit${u}`]
      if (!nextTotal) return { unit: unitNum, lesson: Math.min(elaTotals[`${gradeId}/unit${unitNum}`] || lessonNum, lessonNum + offset) }
      l = l - total
    }
  }
  return { unit: unitNum, lesson: lessonNum }
}

// For Social Science: sequence-aware resolver including two-step intro (lesson0 then li)
export function resolveSsWithOffset(ssData, gradeId, unitNum, lessonNum, phase, offset, introStep) {
  let u = unitNum
  const seqKey = `${gradeId}/unit${u}`
  const seq = ssData.seq[seqKey] || []
  const numIdx = ssData.numIndex[seqKey] || {}
  let idx
  if (phase === 'intro') {
    if (introStep === 0) {
      idx = seq.findIndex(it => it.id === 'lesson0')
    } else if (introStep === 1) {
      idx = seq.findIndex(it => it.id === 'li')
    }
    if (idx === -1 || idx == null) {
      idx = seq.findIndex(it => it.id === 'lesson0')
      if (idx === -1) idx = seq.findIndex(it => it.id === 'li')
    }
  } else if (phase === 'wrap') {
    idx = seq.findIndex(it => it.id === 'lw')
  } else {
    idx = numIdx[lessonNum] != null ? numIdx[lessonNum] : -1
  }
  if (idx === -1) idx = Math.min(Math.max(0, lessonNum - 1), Math.max(0, seq.length - 1))
  let newIdx = idx + offset
  while (true) {
    if (newIdx >= 0 && newIdx < (ssData.seq[`${gradeId}/unit${u}`] || []).length) {
      const item = ssData.seq[`${gradeId}/unit${u}`][newIdx]
      return { unit: u, id: item?.id || `lesson${lessonNum}` }
    }
    if (newIdx < 0) {
      u -= 1
      if (u < 1) return { unit: unitNum, id: (seq[idx]?.id || `lesson${lessonNum}`) }
      const prevSeq = ssData.seq[`${gradeId}/unit${u}`]
      if (!prevSeq || prevSeq.length === 0) return { unit: unitNum, id: (seq[idx]?.id || `lesson${lessonNum}`) }
      newIdx = prevSeq.length + newIdx
      continue
    }
    u += 1
    const nextSeq = ssData.seq[`${gradeId}/unit${u}`]
    if (!nextSeq || nextSeq.length === 0) return { unit: unitNum, id: (seq[idx]?.id || `lesson${lessonNum}`) }
    newIdx = newIdx - (ssData.seq[`${gradeId}/unit${u-1}`]?.length || 0)
  }
}
